import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Progress } from 'antd';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import styles from '../Timeline/styles.module.scss';

import { ImageBox } from './index.jsx';

const ImageBoxes = ({
  mergedDates,
  urls,
  urlsCompared,
  sensor,
  sensorCompared,
  sensorFullDataCompared,
  changeTime,
  changeSecondaryTime,
  isVegetation = false,
  t,
}) => {
  const { selectedSensor, selectedSecondarySensor } = useSelector((state) => state.sensors);

  const [progressLength, setProgressLength] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loadingArchive, setLoadingArchive] = useState(false);

  const ref = useRef(null);

  // Download file by URL
  async function loadFile(url) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${url}`);
    }
    return response.blob();
  }

  // Function for handling file uploads with a limit on the number of parallel requests
  async function downloadFilesWithLimit(fileUrls, limit = 5, mode = 'night', isCompared = false) {
    let urlsMode = [];
    switch (mode) {
      case 'night':
        urlsMode = fileUrls.night;
        break;
      case 'day':
        urlsMode = fileUrls.day;
        break;
      default:
        urlsMode = fileUrls.ndvi;
    }

    if (!urlsMode.length) {
      console.log('no images');
      return;
    }

    const zip = new JSZip();
    let index = 0;
    setProgress(urlsMode.length);
    setLoadingArchive(true);

    async function processBatch() {
      const batch = urlsMode.slice(index, index + limit);
      const promises = batch.map(async (url) => {
        const blob = await loadFile(url.url);
        zip.file(`${url.date ? url.date.replace(/[: ]/g, '-') : ''}_${url.title}.jpg`, blob);
      });

      await Promise.all(promises);
      index += limit;
      if (index < urlsMode.length) {
        setProgressLength(index);
        await processBatch();
      }
    }

    await processBatch();

    //Generating and downloading an archive
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(
      content,
      `${isCompared && sensorFullDataCompared ? sensorFullDataCompared[0].location?.name : sensor.location.name || 'cropler'}_${mode}.zip`,
    );
    setProgressLength(urlsMode.length);
    setLoadingArchive(false);
  }

  const downloadImages = ({ mode, isCompared }) => {
    downloadFilesWithLimit(isCompared ? urlsCompared : urls, 5, mode, isCompared).then(); // 5 parallel requests
  };

  return (
    <>
      {loadingArchive && (
        <div className={styles.timelineLoadingMessage}>
          {t('sensor.archiveLoading')}
          <Progress className={'progress-popup'} percent={Math.round((progressLength * 100) / progress)} />
        </div>
      )}

      <div className={styles.timelineImage} ref={ref}>
        {selectedSensor && (
          <ImageBox
            locationId={selectedSensor}
            key={selectedSensor}
            firstDate={mergedDates[0]}
            lastDate={mergedDates[mergedDates.length - 1]}
            dates={mergedDates}
            sensor={sensor}
            onClickDownloadImages={(mode) => downloadImages({ mode, isCompared: false })}
            isVegetation={isVegetation}
            changeTime={(value) => changeTime(value)}
            t={t}
          />
        )}
        {selectedSecondarySensor && (
          <ImageBox
            locationId={selectedSecondarySensor}
            key={selectedSecondarySensor}
            firstDate={mergedDates[0]}
            lastDate={mergedDates[mergedDates.length - 1]}
            dates={mergedDates}
            sensor={sensorCompared}
            onClickDownloadImages={(mode) => downloadImages({ mode, isCompared: true })}
            isVegetation={isVegetation}
            changeTime={(value) => changeSecondaryTime(value)}
            t={t}
          />
        )}
      </div>
    </>
  );
};

export default ImageBoxes;
